import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { mergeExistingFields } from "helpers/mergeExistingFields";

import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import { ProvidersBunchPayload } from "../../gameCenter/providers/slice";
import { ApiErrorPayload } from "types/ApiErrorPayload";
import NoImage from "../../../assets/images/image_not_found.webp";
import {
  getChangedFields,
  normalizeOptionalText,
} from "helpers/getChangedFields";

export interface PromocodeItem {
  id: number;
  code: string;
  title: string;
  description?: string;
  type: string;
  activationArea?: string;
  targetDomain?: string;
  applicationMode?: string;
  maxUsedCount: number;
  currentUsedCount: number;
  maxUsesPerUser?: number;
  startsAt?: string;
  endsAt?: string;
  image: string | File;
  isActive: boolean | number;
}

export interface BonusMaskItem {
  id?: number;
  promocodeId?: number;

  targetBonusId?: number;

  activationArea?: string;
  currency?: string;

  isActive?: boolean | number;

  title?: string;
  description?: string;

  image?: string | File;
  mobileImage?: string | File;

  payoutMultiplier?: number;
  wagerMultiplier?: number;

  depositPercentageValue?: string;
  depositPercentageMax?: string;

  depositStaticAmount?: string;

  freeSpinsGameId?: number;
  freeSpinsSpinAmount?: string;
  freeSpinsTotalAmount?: number;
  freeSpinsMaxWin?: string;
  freeSpinsGameTitle?: string;
  freeSpinsGameImage?: string;
}

export interface OriginalBonusItem {
  id: number;

  title?: string;
  description?: string;

  image?: string;
  mobileImage?: string;

  payoutMultiplier?: number;
  wagerMultiplier?: number;

  rewards?: any[];

  currencies?: string[];
}

interface PromocodeState {
  list: PromocodeItem[];
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
  isItemFetched: boolean;
  selectedItems: number[];
  payload?: PromocodeItem;
  initialPayload?: PromocodeItem;
  bonusMask?: BonusMaskItem;
  originalBonus?: OriginalBonusItem;
  isItemFetching: boolean;
  isBonusMaskLoading: boolean;
  processingItemId?: PromocodeItem["id"];

  bonusOptions: {
    label: string;
    value: number;
    img?: string;
  }[];

  isBonusOptionsLoading: boolean;
  selectedBonusId?: number;
}

interface GetListPayload {
  limit: number;
  offset: number;
  title: string;
  code: string;
  type?: string;
  isActive?: number;
  order: {
    title?: string;
    code?: string;
    id?: string;
    isActive?: string;
    maxUsedCount?: string;
    currentUsedCount?: string;
  };
}

interface GetBonusMaskListPayload {
  limit: number;
  offset: number;
  promocodeId: number;
  targetBonusId?: number;
  currency: string;
}

const initialState: PromocodeState = {
  list: [],
  isListFetched: false,
  isItemFetched: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  selectedItems: [],
  isItemFetching: false,
  isBonusMaskLoading: false,

  bonusOptions: [],
  isBonusOptionsLoading: false,
  selectedBonusId: undefined,
};

export const getPromocodesList = createAsyncThunk(
  "promocodes/list",
  async (data: GetListPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.promoCodeList}`, {
        ...data,
      });
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const getBonusMaskList = createAsyncThunk(
  "promocodes/bonus-mask-list",
  async (data: GetBonusMaskListPayload) => {
    try {
      const result = await instance.post(
        apiRoutes.promoCodeBonusMaskList,
        data,
      );

      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const getBonusMaskItem = createAsyncThunk(
  "promocodes/bonus-mask-item",
  async (id: string | number) => {
    try {
      const result = await instance.get(
        `${apiRoutes.promoCodeBonusMaskItem}/${id}`,
      );

      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const getBonusOptions = createAsyncThunk(
  "promocodes/bonus-options",
  async (search?: string) => {
    try {
      const result = await instance.post(apiRoutes.bonusesList, {
        limit: 10,
        offset: 0,
        statusId: 3,
        title: search ?? undefined,
        order: {
          id: "desc",
          type: "desc",
          title: "asc",
        },
      });

      return result.data?.items ?? [];
    } catch (error) {
      return [];
    }
  },
);

export const getOriginalBonus = createAsyncThunk(
  "promocodes/original-bonus",
  async (id: number) => {
    try {
      const result = await instance.get(`${apiRoutes.bonusItem}/${id}`);

      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const createPromocode = createAsyncThunk<
  number,
  PromocodeItem,
  { rejectValue: ApiErrorPayload }
>("promocodes/create", async (data, { rejectWithValue }) => {
  try {
    const response = await instance.post(apiRoutes.promoCodeCreate, data);

    return response.data.id;
  } catch (error: any) {
    const apiError = error?.data?.error ?? error?.data;

    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

export const updatePromocode = createAsyncThunk<
  boolean,
  PromocodeItem,
  { rejectValue: ApiErrorPayload }
>("promocodes/update", async (data, { rejectWithValue, getState }) => {
  try {
    const state = getState() as PromocodeState | { promocodes: PromocodeState };
    const initialPayload =
      "promocodes" in state
        ? state.promocodes.initialPayload
        : state.initialPayload;
    const changes = getChangedFields(
      initialPayload,
      data,
      [
        "title",
        "code",
        "description",
        "type",
        "activationArea",
        "targetDomain",
        "applicationMode",
        "maxUsedCount",
        "currentUsedCount",
        "maxUsesPerUser",
        "startsAt",
        "endsAt",
        "isActive",
      ],
      {
        title: normalizeOptionalText,
        code: normalizeOptionalText,
        description: normalizeOptionalText,
      },
    );

    await instance.put(`${apiRoutes.promoCodeUpdate}/${data.id}`, {
      ...changes,
    });

    return true;
  } catch (error: any) {
    const apiError = error?.data?.error ?? error?.data;

    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

export const createBonusMask = createAsyncThunk<
  number,
  BonusMaskItem,
  { rejectValue: ApiErrorPayload }
>(
  "promocodes/bonus-mask-create",
  async (payload: BonusMaskItem, { rejectWithValue }) => {
    const data = new FormData();

    if (payload.title) {
      data.append("title", payload.title);
    }

    if (payload.description) {
      data.append("description", payload.description);
    }
    if (payload.payoutMultiplier !== undefined) {
      data.append("payoutMultiplier", payload.payoutMultiplier.toString());
    }

    if (payload.wagerMultiplier !== undefined) {
      data.append("wagerMultiplier", payload.wagerMultiplier.toString());
    }
    if (payload.depositPercentageValue) {
      data.append("depositPercentageValue", payload.depositPercentageValue);
    }

    if (payload.depositPercentageMax) {
      data.append("depositPercentageMax", payload.depositPercentageMax);
    }

    if (payload.depositStaticAmount) {
      data.append("depositStaticAmount", payload.depositStaticAmount);
    }

    if (payload.freeSpinsGameId !== undefined) {
      data.append("freeSpinsGameId", payload.freeSpinsGameId.toString());
    }

    if (payload.freeSpinsSpinAmount) {
      data.append("freeSpinsSpinAmount", payload.freeSpinsSpinAmount);
    }

    if (payload.freeSpinsTotalAmount !== undefined) {
      data.append(
        "freeSpinsTotalAmount",
        payload.freeSpinsTotalAmount.toString(),
      );
    }

    if (payload.freeSpinsMaxWin) {
      data.append("freeSpinsMaxWin", payload.freeSpinsMaxWin);
    }

    if (payload.image instanceof File) {
      data.append("image", payload.image);
    }

    if (payload.mobileImage instanceof File) {
      data.append("mobileImage", payload.mobileImage);
    }

    if (payload.promocodeId !== undefined) {
      data.append("promocodeId", payload.promocodeId.toString());
    }

    if (payload.targetBonusId !== undefined) {
      data.append("targetBonusId", payload.targetBonusId.toString());
    }

    if (payload.activationArea) {
      data.append("activationArea", payload.activationArea);
    }

    if (payload.currency) {
      data.append("currency", payload.currency);
    }

    data.append("isActive", payload.isActive ? "1" : "0");

    try {
      const response = await instance.post(
        apiRoutes.promoCodeBonusMaskCreate,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return response.data.id;
    } catch (error: any) {
      const apiError = error?.data?.error ?? error?.data;

      if (apiError?.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);

export const updateBonusMask = createAsyncThunk<
  boolean,
  BonusMaskItem,
  { rejectValue: ApiErrorPayload }
>(
  "promocodes/bonus-mask-update",
  async (payload: BonusMaskItem, { rejectWithValue }) => {
    const data = new FormData();

    if (payload.title) {
      data.append("title", payload.title);
    }

    if (payload.description) {
      data.append("description", payload.description);
    }
    if (payload.payoutMultiplier !== undefined) {
      data.append("payoutMultiplier", payload.payoutMultiplier.toString());
    }

    if (payload.wagerMultiplier !== undefined) {
      data.append("wagerMultiplier", payload.wagerMultiplier.toString());
    }
    if (payload.depositPercentageValue) {
      data.append("depositPercentageValue", payload.depositPercentageValue);
    }

    if (payload.depositPercentageMax) {
      data.append("depositPercentageMax", payload.depositPercentageMax);
    }

    if (payload.depositStaticAmount) {
      data.append("depositStaticAmount", payload.depositStaticAmount);
    }

    if (payload.freeSpinsGameId !== undefined) {
      data.append("freeSpinsGameId", payload.freeSpinsGameId.toString());
    }

    if (payload.freeSpinsSpinAmount) {
      data.append("freeSpinsSpinAmount", payload.freeSpinsSpinAmount);
    }

    if (payload.freeSpinsTotalAmount !== undefined) {
      data.append(
        "freeSpinsTotalAmount",
        payload.freeSpinsTotalAmount.toString(),
      );
    }

    if (payload.freeSpinsMaxWin) {
      data.append("freeSpinsMaxWin", payload.freeSpinsMaxWin);
    }

    if (payload.image instanceof File) {
      data.append("image", payload.image);
    }

    if (payload.mobileImage instanceof File) {
      data.append("mobileImage", payload.mobileImage);
    }

    if (payload.promocodeId !== undefined) {
      data.append("promocodeId", payload.promocodeId.toString());
    }

    if (payload.targetBonusId !== undefined) {
      data.append("targetBonusId", payload.targetBonusId.toString());
    }

    if (payload.activationArea) {
      data.append("activationArea", payload.activationArea);
    }

    if (payload.currency) {
      data.append("currency", payload.currency);
    }

    data.append("isActive", payload.isActive ? "1" : "0");

    try {
      await instance.put(
        `${apiRoutes.promoCodeBonusMaskUpdate}/${payload.id}`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return true;
    } catch (error: any) {
      const apiError = error?.data?.error ?? error?.data;

      if (apiError?.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);

export const getPromocodeItem = createAsyncThunk(
  "promocodes/read",
  async (id: string | number) => {
    try {
      const result = await instance.get(`${apiRoutes.promoCodeItem}/${id}`);
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const deletePromocodeItem = createAsyncThunk(
  "promocodes/delete",
  async (id: string | number) => {
    try {
      await instance.delete(`${apiRoutes.promoCodeDelete}/${id}`);
      return true;
    } catch (error) {
      return false;
    }
  },
);

export const updatePromocodeBunch = createAsyncThunk(
  "game-center/providers/update-bunch",
  async (data: ProvidersBunchPayload) => {
    try {
      await instance.put(`${apiRoutes.promoCodeUpdateBunch}`, {
        ...data,
        isActive: data.isActive,
      });
      return true;
    } catch (error) {
      return false;
    }
  },
);

export const promocodesSlice = createSlice({
  name: "promocodes",
  initialState,
  reducers: {
    handlePaginate: (state: PromocodeState, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    handleUpdateFields: (
      state: PromocodeState,
      action: PayloadAction<PromocodeItem>,
    ) => {
      state.payload = { ...state.payload, ...action.payload };
    },
    handleClearFields: (state: PromocodeState) => {
      state.payload = undefined;
      state.initialPayload = undefined;
      state.bonusMask = undefined;
      state.originalBonus = undefined;
      state.selectedBonusId = undefined;
      state.isItemFetched = false;
    },
    handleSelectItem: (
      state: PromocodeState,
      action: PayloadAction<number>,
    ) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems = [...state.selectedItems, action.payload];
      }
    },
    handleSelectAll: (state: PromocodeState) => {
      if (state.selectedItems.length === state.list.length) {
        state.selectedItems = [];
      } else {
        state.selectedItems = state.list.map((el) => el.id ?? 0);
      }
    },
    handleUpdateBonusMaskFields: (
      state,
      action: PayloadAction<Partial<BonusMaskItem>>,
    ) => {
      state.bonusMask = {
        ...state.bonusMask,
        ...action.payload,
      };
    },
    handleSetOriginalBonus: (
      state,
      action: PayloadAction<OriginalBonusItem | undefined>,
    ) => {
      state.originalBonus = action.payload;
    },
    handleSetSelectedBonusId: (
      state,
      action: PayloadAction<number | undefined>,
    ) => {
      state.selectedBonusId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getPromocodesList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(createPromocode.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(createPromocode.fulfilled, (state) => {
      state.isListFetched = false;
      state.isItemFetching = false;
    });
    builder.addCase(createPromocode.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(updatePromocode.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updatePromocode.fulfilled, (state, action) => {
      state.list = state.list.map((item) =>
        item.id === action.meta.arg.id
          ? mergeExistingFields(item, {
              ...action.meta.arg,
              image:
                action.meta.arg.image instanceof File
                  ? item.image
                  : action.meta.arg.image,
            })
          : item,
      );
      state.isItemFetching = false;
      state.processingItemId = undefined;
      state.initialPayload = state.payload ? { ...state.payload } : undefined;
    });
    builder.addCase(updatePromocode.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(deletePromocodeItem.pending, (state, action) => {
      state.processingItemId = +action.meta.arg || undefined;
    });
    builder.addCase(deletePromocodeItem.fulfilled, (state) => {
      state.isListFetched = false;
      state.processingItemId = undefined;
    });
    builder.addCase(deletePromocodeItem.rejected, (state) => {
      state.processingItemId = undefined;
    });
    builder.addCase(updatePromocodeBunch.fulfilled, (state) => {
      state.isListFetched = false;
      state.selectedItems = [];
    });
    builder.addCase(getPromocodeItem.fulfilled, (state, action) => {
      state.isItemFetched = true;
      state.payload = { ...action.payload, isActive: action.payload ? 1 : 0 };
      state.initialPayload = action.payload
        ? ({ ...state.payload } as PromocodeItem)
        : undefined;
    });
    builder.addCase(getBonusMaskList.pending, (state) => {
      state.isBonusMaskLoading = true;
    });
    builder.addCase(getBonusMaskList.fulfilled, (state, action) => {
      state.isBonusMaskLoading = false;
      const item = action.payload?.items?.[0];

      state.bonusMask = item
        ? {
            ...item,
            image: item.imageCdn,
            mobileImage: item.mobileImageCdn,
          }
        : undefined;

      if (item?.targetBonusId) {
        state.selectedBonusId = item.targetBonusId;
      }
    });
    builder.addCase(getBonusMaskList.rejected, (state) => {
      state.isBonusMaskLoading = false;
    });
    builder.addCase(getBonusMaskItem.fulfilled, (state, action) => {
      state.bonusMask = action.payload;
    });
    builder.addCase(createBonusMask.pending, (state) => {
      state.isItemFetching = true;
    });

    builder.addCase(createBonusMask.fulfilled, (state, action) => {
      state.isItemFetching = false;
      state.bonusMask = {
        ...action.meta.arg,
        id: action.payload,
      };
    });

    builder.addCase(createBonusMask.rejected, (state) => {
      state.isItemFetching = false;
    });

    builder.addCase(updateBonusMask.pending, (state) => {
      state.isItemFetching = true;
    });

    builder.addCase(updateBonusMask.fulfilled, (state) => {
      state.isItemFetching = false;
    });

    builder.addCase(updateBonusMask.rejected, (state) => {
      state.isItemFetching = false;
    });

    builder.addCase(getBonusOptions.pending, (state) => {
      state.isBonusOptionsLoading = true;
    });

    builder.addCase(getBonusOptions.fulfilled, (state, action) => {
      state.isBonusOptionsLoading = false;

      state.bonusOptions = action.payload.map((item: any) => ({
        label: item.title,
        value: item.id,
        img: item.imageSmall || item.image || NoImage,
      }));
    });

    builder.addCase(getBonusOptions.rejected, (state) => {
      state.isBonusOptionsLoading = false;
      state.bonusOptions = [];
    });
    builder.addCase(getOriginalBonus.fulfilled, (state, action) => {
      if (!action.payload) {
        return;
      }

      state.originalBonus = {
        id: action.payload.id,
        title: action.payload.title,
        description: action.payload.description,
        image: action.payload.image,
        mobileImage: action.payload.mobileImage,
        payoutMultiplier: action.payload.payoutX,
        wagerMultiplier: action.payload.wagerX,
        rewards: action.payload.rewards,
        currencies: action.payload.currencies,
      };
    });
  },
});

export const {
  handlePaginate,
  handleSelectItem,
  handleSelectAll,
  handleUpdateFields,
  handleClearFields,
  handleUpdateBonusMaskFields,
  handleSetOriginalBonus,
  handleSetSelectedBonusId,
} = promocodesSlice.actions;
